import { DownOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Dropdown,
  Menu,
  message,
  Row,
  Spin,
  Table,
  TableProps,
} from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { SorterResult } from 'antd/lib/table/interface';
import {
  DonorStatus,
  DvmExamListFilterInput,
  DvmExamListSort,
  DvmExamOutput,
  Order,
  useGetDvmExamListLazyQuery,
  useGetDvmExamListQuery,
} from 'gql/types';
import { TableSearchInput } from 'modules/common/components';
import { useDebounce, useFilter, useSort } from 'modules/common/hooks';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useState } from 'react';
import { getTranslation } from 'utils/getTranslation';

import { getColumns } from './column';
import { DvmExamExportFormat, exportDvmExams } from './export';
import { FilterBar } from './FilterBar';

// Backend caps take at 2000 per request; export pages through the full set
const EXPORT_PAGE_SIZE = 2000;
const EXPORT_MAX_ROWS = 20000;

export const GeneralDvmExamTable = () => {
  const t = getTranslation('donor.GeneralDvmExamTable');
  const { debounce } = useDebounce();
  const pagination = usePagination({ showSizeChanger: true });
  // Soonest-due donors first by default
  const dvmExamSort = useSort<DvmExamListSort>([
    { fieldName: DvmExamListSort.NextExamDue, order: Order.Asc },
  ]);
  const dvmExamFilter = useFilter<DvmExamListFilterInput>([
    {
      fieldName: 'donorStatuses',
      value: [
        DonorStatus.Active,
        DonorStatus.LabworkPending,
        DonorStatus.Approved,
        DonorStatus.AnnualLabworkDue,
      ],
    },
  ]);
  const [isExporting, setIsExporting] = useState(false);

  // The general page shows one row per donor (their latest exam); the
  // per-donor history tab shares this query without the flag.
  const filter: DvmExamListFilterInput = {
    ...dvmExamFilter.getFilterObject(),
    latestPerDonor: true,
  };

  const { data, loading: getDvmExamListLoading } = useGetDvmExamListQuery({
    variables: {
      input: {
        filter,
        take: pagination.pageSize,
        skip: pagination.skip,
        sort: dvmExamSort.options,
      },
    },
    onCompleted: (result) => {
      pagination.setTotal(result.getDvmExamList.meta.total);
    },
  });

  const [getDvmExamListForExport] = useGetDvmExamListLazyQuery({
    fetchPolicy: 'network-only',
  });

  const isLoading = getDvmExamListLoading;
  const dvmExams = data?.getDvmExamList.data as DvmExamOutput[];

  const onTableChange: TableProps<DvmExamOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<DvmExamOutput>;
      const fieldName = tableSorter.columnKey as DvmExamListSort;
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        dvmExamSort.setSortOption({ fieldName, order });
      }
      if (!tableSorter.order) {
        dvmExamSort.removeSortOption(fieldName);
      }
    }

    if (extra.action === 'filter') {
      Object.keys(filters).forEach((fieldName) => {
        dvmExamFilter?.setFilterOption({
          fieldName: fieldName as keyof DvmExamListFilterInput,
          value: (filters as any)[fieldName],
        });
      });
    }
  };

  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    dvmExamFilter.setFilterOption({
      fieldName: 'searchingQuery',
      value,
    });
  };

  const handleFilterChange = (
    fieldName: keyof DvmExamListFilterInput,
    value: unknown
  ) => {
    pagination.moveTo(1);
    if (value === undefined) {
      dvmExamFilter.removeFilterOption(fieldName);
    } else {
      dvmExamFilter.setFilterOption({ fieldName, value });
    }
  };

  const handleClearFilters = () => {
    pagination.moveTo(1);
    dvmExamFilter.clearFilters(['searchingQuery']);
  };

  const handleExport = async (exportFormat: DvmExamExportFormat) => {
    setIsExporting(true);
    try {
      const exams: DvmExamOutput[] = [];
      let total = Infinity;
      while (exams.length < Math.min(total, EXPORT_MAX_ROWS)) {
        const result = await getDvmExamListForExport({
          variables: {
            input: {
              filter,
              take: EXPORT_PAGE_SIZE,
              skip: exams.length,
              sort: dvmExamSort.options,
            },
          },
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        const page = (result.data?.getDvmExamList.data ??
          []) as DvmExamOutput[];
        total = result.data?.getDvmExamList.meta.total ?? 0;
        if (page.length === 0) break;
        exams.push(...page);
      }
      if (exams.length === 0) {
        message.info(t('emptyExport'));
        return;
      }
      exportDvmExams(exams, exportFormat);
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const { md } = useBreakpoint();
  const columns = getColumns({
    showSorterTooltip: md,
    sortOptions: dvmExamSort.options,
  });

  return (
    <Spin spinning={isLoading}>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <FilterBar
            filter={dvmExamFilter.getFilterObject()}
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
          />
        </Col>
        <Col span={24}>
          <Row gutter={[8, 8]} align="middle" wrap={false}>
            <Col flex="auto">
              <TableSearchInput
                onSearchChange={debounce(handleSearch)}
                placeholder={t('searchDvmExam')}
                maxLength={100}
              />
            </Col>
            <Col>
              <Dropdown
                disabled={isExporting}
                overlay={
                  <Menu
                    onClick={({ key }) =>
                      handleExport(key as DvmExamExportFormat)
                    }
                    items={[
                      { key: 'csv', label: t('exportCsv') },
                      { key: 'xlsx', label: t('exportExcel') },
                    ]}
                  />
                }
              >
                <Button loading={isExporting}>
                  {t('export')} <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Table
            rowKey={({ id }) => id}
            columns={columns}
            dataSource={dvmExams}
            onChange={onTableChange}
            pagination={pagination.config}
            scroll={{ x: 900 }}
          />
        </Col>
      </Row>
    </Spin>
  );
};
